import { forwardRef, useEffect, useRef, type ButtonHTMLAttributes, type MouseEvent } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../lib/cn'

/**
 * The shared action primitive (audit UI-17). Every property here exists
 * because its absence was a separate audit finding across the 800 hand-styled
 * buttons this replaces:
 *
 * - **Focus chrome** (UI-4): `focus-visible:ring-ring` — the `--ring` token was
 *   defined and tuned in globals.css and then applied by nothing. The ring is
 *   keyboard-only (`focus-visible`), offset so it reads outside the fill, and
 *   `--ring` tracks `--primary` in both themes at ≥3:1 against the page.
 * - **Tap-target floor** (UI-6, D28): `default`/`icon` sizes are ≥44px — the
 *   gloved-hands floor. `sm` (~36px) is the dense-toolbar opt-in and is
 *   FORBIDDEN on operator-facing run screens; `inline` is for buttons living
 *   inside running text or table cells, where WCAG 2.5.8's inline exception
 *   applies and a 44px box would break the line.
 * - **Pending state** (UI-15): `pending` disables the control, sets
 *   `aria-busy`, and shows a spinner while KEEPING the label — a double-tap on
 *   a slow floor network was firing audited, e-signed mutations twice.
 * - **`type` defaults to `"button"`**, not the platform's `"submit"` — an
 *   action button inside a form must not submit it by accident. Form submit
 *   buttons say `type="submit"` explicitly.
 *
 * Raw `<button>` in feature code is a lint error (`no-raw-buttons`); this
 * component is the one place button chrome is decided.
 */
const button = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-primary font-medium text-primary-foreground hover:bg-primary/90',
        secondary: 'bg-secondary font-medium text-secondary-foreground hover:bg-secondary/80',
        outline: 'border bg-background hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        destructive: 'bg-destructive font-medium text-destructive-foreground hover:bg-destructive/90',
        /** In-text action styled as a link. Pair with `size="inline"`. */
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        /**
         * The floor default: 44px min height (D28 gloved-hands floor) at
         * comfortable density. At COMPACT density (Fiix audit §8.2) the VISUAL
         * height follows `--control-h` (36 px) while the `before:` pseudo keeps
         * a 44 px hit area centred on the control — WCAG 2.5.8 and D28 hold
         * for a pointer that lands 4 px above a dense toolbar button.
         */
        default: "relative min-h-control px-4 py-2 text-sm before:absolute before:inset-x-0 before:top-1/2 before:h-11 before:-translate-y-1/2 before:content-['']",
        /** Dense toolbars/filter bars only — NOT operator run screens (UI-6). */
        sm: "relative min-h-9 px-3 py-1.5 text-sm before:absolute before:inset-x-0 before:top-1/2 before:h-11 before:-translate-y-1/2 before:content-['']",
        /** Icon-only, 44px square (36 at compact density, same hit-area rule). The accessible name is on the caller. */
        icon: "relative h-control w-control shrink-0 before:absolute before:left-1/2 before:top-1/2 before:h-11 before:w-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']",
        /** Icon-only dense variant — same rule as `sm`. */
        iconSm: 'h-9 w-9 shrink-0',
        /**
         * Flows with surrounding text. FF-AUDIT-092: a 24 px minimum hit area
         * (WCAG 2.5.8 target-size minimum) — row actions like Reopen/Archive
         * measured 16 px tall on a phone. The negative horizontal margin keeps
         * the visible text where it was while the target grows around it.
         * 44 px for row actions would need a per-row menu — owner decision,
         * recorded under FF-AUDIT-092.
         */
        inline: 'min-h-6 rounded-sm px-1 -mx-1 py-0 text-sm',
        /** A control inside a table cell (density standard, 2026-09-18): 24 px visible so the row stays on the 36 px grid, the 44 px hit area on the `before:` pseudo as on `RowMenu size="cell"`. */
        cell: "relative h-6 px-2 text-xs before:absolute before:inset-x-0 before:top-1/2 before:h-11 before:-translate-y-1/2 before:content-['']",
      },
    },
    defaultVariants: { variant: 'primary', size: 'default' },
  },
)

/**
 * The button chrome as a class string, for the one case a `<button>` is the
 * wrong ELEMENT: a control that navigates is a link, and a link is what a
 * screen reader, a middle-click and a "copy link address" all need it to be.
 *
 * Mirrors `inputClass` in `field.tsx`, which exists for the same reason. There
 * is deliberately no `asChild` prop: cloning an arbitrary child to inject props
 * is how a `<Link>` ends up with `type="button"` and `disabled` attributes that
 * mean nothing on an anchor.
 */
export function buttonClass(options: VariantProps<typeof button> & { className?: string } = {}): string {
  const { className, ...variants } = options
  return cn(button(variants), className)
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof button> & {
    /**
     * True while the action this button fires is in flight: disables the
     * control, sets `aria-busy`, and prepends a spinner without collapsing the
     * label (a button that blanks to a spinner loses its width and its
     * meaning). Wire it straight to `mutation.isPending`.
     */
    pending?: boolean | undefined
  }

/** Inline SVG spinner — packages/ui has no icon dependency by design. */
function Spinner() {
  return (
    <svg
      className="size-4 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z"
      />
    </svg>
  )
}

/**
 * Same-burst re-entrancy window (FF-AUDIT-093). `pending` / `disabled` take
 * effect only once React has re-rendered with them; a double/triple tap that
 * lands in the same tick — a gloved hand on a laggy screen — activated a
 * create three times before the first render, and three NCRs were numbered.
 * For a button that models an action — a form submit, or one whose caller
 * binds `pending` or `disabled` (both are how a dialog says "an async action
 * lives here") — activations within this window of the first are swallowed;
 * a `pending` that was seen true and then false ends the window early, since
 * the action demonstrably finished. Plain buttons with neither are untouched.
 */
const REENTRY_WINDOW_MS = 500

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, pending, type = 'button', disabled, className, children, onClick, ...rest },
  ref,
) {
  const lastActivation = useRef(0)
  const sawPending = useRef(false)
  useEffect(() => {
    if (pending) sawPending.current = true
    else if (sawPending.current) {
      // The declared action completed — the next activation is a new one.
      sawPending.current = false
      lastActivation.current = 0
    }
  }, [pending])
  const guardedClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (pending !== undefined || disabled !== undefined || type === 'submit') {
      const now = Date.now()
      if (lastActivation.current && now - lastActivation.current < REENTRY_WINDOW_MS) {
        event.preventDefault()
        event.stopPropagation()
        return
      }
      lastActivation.current = now
    }
    onClick?.(event)
  }
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || pending}
      aria-busy={pending || undefined}
      className={cn(button({ variant, size }), className)}
      onClick={guardedClick}
      {...rest}
    >
      {pending && <Spinner />}
      {children}
    </button>
  )
})
